import { RequireAtLeastOne, ValueOf } from 'type-fest'
import React, { PropsWithChildren, useCallback } from 'react'
import noop from 'lib/function/noop'
import useStableObjectReference from 'hooks/useStableObjectReference'
import * as Analytics from 'analytics/analytics'
import { destinationClickEventWithContext, destinationImpressionEventWithContext, impressionEventWithContext, productClickEventWithContext, SnowplowDestinationContext } from 'analytics/snowplow/events'
import { EventDataKey } from 'home/pages/HomePage/useHomepageAnalytics'

export type TrackableProduct = App.AnyOffer | App.ExperienceOffer | App.FlightDeal | App.JourneyV2 | App.CarHireOffer | App.MerchandiseOffer
export enum OfferListEvents {
  productClick,
  productImpression,
  listLoad,
  clientAction,
  productHover,
  offerReady,
  pricingCalculated,
  destinationImpression,
  destinationClick,
}

export type OfferListEventsDispatchAction = ValueOf<Utils.FullActionMap<{
  [OfferListEvents.productClick]: {
    offer: TrackableProduct
    position: number
    price?: number
    memberPrice?: number
    duration?: number
    availability?: boolean
    /**
     * @deprecated DO NOT USE. THIS IS FOR HOMEPAGE ONLY!
     */
    key?: string
    extendedAvailability?: number
  }
  [OfferListEvents.productImpression]: {
    offer: TrackableProduct
    position: number
    availability?: boolean
    price?: number
    memberPrice?: number
    duration?: number
    /**
     * @deprecated DO NOT USE. THIS IS FOR HOMEPAGE ONLY!
     */
    key?: string
    extendedAvailability?: number
  }
  [OfferListEvents.offerReady]: {}
  [OfferListEvents.listLoad]: {
    key: string
    list: App.OfferList
  }
  [OfferListEvents.productHover]: {
    offer: TrackableProduct
  }
  [OfferListEvents.clientAction]: {
    action: string
    subject: string
    /**
     * @deprecated DO NOT USE. THIS IS FOR HOMEPAGE ONLY!
     */
    key?: string
  }
  [OfferListEvents.pricingCalculated]: {
    availability: boolean
    price: number | undefined
    memberPrice: number | undefined
    duration: number | undefined
    extendedAvailability?: App.OfferListMetaData['extendedAvailability']
  }
  [OfferListEvents.destinationImpression]: {
    position: number
    destination: SnowplowDestinationContext
    key: EventDataKey.DestinationImpression
    filter?: string
  }
  [OfferListEvents.destinationClick]: {
    position: number
    destination: SnowplowDestinationContext
    key: EventDataKey.DestinationClick
    filter?: string
  }
}>>

export type AdditionalData = Record<string | number | symbol, any>
export type OfferListEventHandler<Data extends AdditionalData = AdditionalData> = (
  dispatchAction: OfferListEventsDispatchAction,
  additionalData?: Data,
) => void
const OfferListEventsContext = React.createContext<OfferListEventHandler>(noop)

type Props = RequireAtLeastOne<{
  /**
   * Event that will be fired whenever an action occurs in the offer list
   */
  onListEvent?: OfferListEventHandler
  /**
   * Use this to augment any data to the `onListEvent` function
   */
  additionalData?: AdditionalData
  /**
   * Pass a tracking config object to enable default tracking.
   * This currently will fire analytics for:
   * - Product clicks
   * - Impressions
   *
   * Use this to automatically track the 'common' metrics
   * For any extra metrics, please hook into the `onListEvent` prop
   */
  tracking?: App.OfferListTrackingConfig
}, 'onListEvent' | 'tracking'>

export function OfferListEventsProvider(props: PropsWithChildren<Props>) {
  const { onListEvent = noop, additionalData, tracking } = props
  const stableAdditionalData = useStableObjectReference(additionalData)

  const value = useCallback<OfferListEventHandler>((dispatchAction) => {
    onListEvent(dispatchAction, stableAdditionalData)

    if (tracking) {
      switch (dispatchAction.type) {
        case (OfferListEvents.productClick): {
          const {
            offer,
            position,
            price,
            memberPrice,
            duration,
          } = dispatchAction

          let finalTracking = tracking

          // If the individual offer has different sources, will map the source here
          if (tracking.offerSourceMapping) {
            const offerSource = tracking.offerSourceMapping[offer.id]
            finalTracking = {
              ...tracking,
              listSource: offerSource ?? tracking.listSource,
            }
          }
          if (tracking?.snowplow?.enabled) {
            Analytics.trackEvent(
              productClickEventWithContext(
                position + 1,
                finalTracking.listName,
                tracking.listId,
                offer,
                { price, memberPrice, duration },
              ),
            )
          }

          break
        }
        case (OfferListEvents.destinationClick): {
          const {
            destination,
            position,
            filter,
          } = dispatchAction
          if (tracking.snowplow?.enabled) {
            Analytics.trackEvent(
              destinationClickEventWithContext(
                position + 1,
                tracking.listName,
                tracking.listId,
                destination,
                filter,
              ),
            )
          }
          break
        }
        case (OfferListEvents.destinationImpression): {
          const {
            destination,
            position,
            filter,
          } = dispatchAction
          if (tracking.snowplow?.enabled) {
            Analytics.trackEvent(
              destinationImpressionEventWithContext(
                position + 1,
                tracking.listName,
                tracking.listId,
                destination,
                filter,
              ),
            )
          }
          break
        }
        case (OfferListEvents.productImpression): {
          const {
            offer,
            position,
            price,
            memberPrice,
            duration,
          } = dispatchAction
          // If the individual offer has different sources, will map the source here
          let finalTracking = tracking
          if (tracking.offerSourceMapping) {
            const offerSource = tracking.offerSourceMapping[offer.id]
            finalTracking = { ...tracking, listSource: offerSource ?? tracking.listSource }
          }
          if (tracking.snowplow?.enabled) {
            Analytics.trackEvent(
              impressionEventWithContext(
                position + 1,
                finalTracking.listName,
                tracking.listId,
                offer,
                { price, memberPrice, duration },
              ),
            )
          }
          break
        }
      }
    }
  }, [onListEvent, stableAdditionalData, tracking])

  return <OfferListEventsContext.Provider value={value}>
    {props.children}
  </OfferListEventsContext.Provider>
}

export default OfferListEventsContext
